import type { Comment } from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import { CommentStatus } from '@getaccept/lib-shared/src/contextual-comments/types/comment-status';
import type { CreateCommentDto } from '@getaccept/lib-shared/src/contextual-comments/types/create-comment-dto';
import type { UpdateCommentDto } from '@getaccept/lib-shared/src/contextual-comments/types/update-comment-dto';
import type { CreateReplyInput } from '@getaccept/lib-shared/src/contextual-comments/types/create-reply-input';
import type { SetContextualCommentAndRepliesReadbyInput } from '@getaccept/lib-shared/src/contextual-comments/types/set-contextual-comment-and-replies-readby-input';
import { apolloClient } from '../../../apollo';
import { CommentMutations } from '../graphql/comment.mutations';
import { CommentQueries } from '../graphql/comment.queries';

export class CommentService {
  static async create(payload: CreateCommentDto): Promise<Comment> {
    const {
      data: { createContextualComment },
    } = await apolloClient.mutate({
      mutation: CommentMutations.create,
      variables: {
        payload,
      },
    });
    return createContextualComment;
  }

  static async load(
    statuses: CommentStatus[] = [CommentStatus.Draft, CommentStatus.Active, CommentStatus.Resolved],
    limit?: number,
    offset?: number
  ) {
    const {
      data: { contextualComments },
    } = await apolloClient.mutate({
      mutation: CommentQueries.load,
      variables: {
        statuses,
        limit,
        offset,
      },
    });
    return contextualComments;
  }

  static async update(payload: Partial<UpdateCommentDto>): Promise<void> {
    await apolloClient.mutate({
      mutation: CommentMutations.update,
      variables: {
        payload,
      },
    });
  }

  static async createReply(payload: CreateReplyInput) {
    const response = await apolloClient.mutate({
      mutation: CommentMutations.createReply,
      variables: {
        payload,
      },
    });

    return response.data.createContextualCommentReply;
  }

  static async readComment(commentId: string) {
    const response = await apolloClient.mutate({
      mutation: CommentMutations.readComment,
      variables: { commentId },
    });
    return response.data.setContextualCommentReadBy;
  }

  static async readReply(replyId: string, commentId: string) {
    const response = await apolloClient.mutate({
      mutation: CommentMutations.readReply,
      variables: { commentId, replyId },
    });
    return response.data.setContextualCommentReplyReadBy;
  }

  static async readCommentsAndReplies(payload: SetContextualCommentAndRepliesReadbyInput) {
    const response = await apolloClient.mutate({
      mutation: CommentMutations.readCommentsAndReplies,
      variables: { payload },
    });
    return response.data.setContextualCommentAndRepliesReadBy;
  }
}
